import { useState } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { toast } from "sonner";
import { FileDown, Menu, Printer, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PrintPortal } from "@/components/PrintPortal";
import { dataBR, num } from "@/lib/estoque";
import { mascararQuantidade, mascararValor } from "@/lib/modo-valores";

export type InsumoDia = {
  id: string;
  nome: string;
  unidade: string;
  qtd: number;
  custo: number;
};

const dinheiro = (v: number) =>
  new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    mascararValor(Number(v) || 0),
  );
const qtde = (v: number, casas = 2) => num(mascararQuantidade(Number(v) || 0), casas);

/** Monta o PDF do relatório "Insumos utilizados no dia". */
function montarPDF(titulo: string, linhas: InsumoDia[]) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  doc.setFontSize(14);
  doc.text(titulo, 40, 40);
  autoTable(doc, {
    startY: 60,
    head: [["Insumo", "Qtde", "Custo R$"]],
    body: linhas.map((i) => [i.nome, `${qtde(i.qtd)} ${i.unidade}`.trim(), dinheiro(i.custo)]),
    foot: [["Total", "", dinheiro(linhas.reduce((a, i) => a + i.custo, 0))]],
    styles: { fontSize: 10 },
    columnStyles: { 1: { halign: "right" }, 2: { halign: "right" } },
  });
  return doc;
}

/**
 * Listagem sumarizada dos insumos consumidos em um dia, com botões de
 * impressão, exportação em PDF e compartilhamento.
 */
export function RelatorioInsumosDia({
  data,
  linhas,
  comIcones = true,
  mostrarTabela = true,
  menuSanduiche = false,
}: {
  data: string;
  linhas: InsumoDia[];
  /** true = botões só com ícones; false = botões só com texto. */
  comIcones?: boolean;
  mostrarTabela?: boolean;
  menuSanduiche?: boolean;
}) {
  const [imprimindo, setImprimindo] = useState(false);
  const titulo = `Insumos utilizados no dia ${dataBR(data)}`;
  const total = linhas.reduce((a, i) => a + i.custo, 0);
  const arquivo = `insumos-utilizados-${data}.pdf`;

  function semDados() {
    if (linhas.length) return false;
    toast.error("Nenhum insumo consumido nesta data.");
    return true;
  }

  function imprimir() {
    if (semDados()) return;
    setImprimindo(true);
    setTimeout(() => {
      window.print();
      setImprimindo(false);
    }, 80);
  }

  function exportarPDF() {
    if (semDados()) return;
    montarPDF(titulo, linhas).save(arquivo);
  }

  async function compartilhar() {
    if (semDados()) return;
    const blob = montarPDF(titulo, linhas).output("blob");
    const file = new File([blob], arquivo, { type: "application/pdf" });
    const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
    if (nav.canShare?.({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: titulo });
        return;
      } catch {
        return;
      }
    }
    montarPDF(titulo, linhas).save(arquivo);
    toast.info("Compartilhamento não disponível; o PDF foi baixado.");
  }

  const botoes = (
    <div className="flex flex-wrap items-center gap-2">
      {menuSanduiche ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon" aria-label="Ações" title="Ações">
              <Menu className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem onSelect={imprimir}>
              <Printer className="h-4 w-4" />
              Imprimir
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={exportarPDF}>
              <FileDown className="h-4 w-4" />
              PDF
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => void compartilhar()}>
              <Share2 className="h-4 w-4" />
              Compartilhar
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <>
          <Button
            variant="outline"
            size={comIcones ? "icon" : "default"}
            onClick={imprimir}
            aria-label="Imprimir"
          >
            {comIcones ? <Printer className="h-4 w-4" /> : "Imprimir"}
          </Button>
          <Button
            variant="outline"
            size={comIcones ? "icon" : "default"}
            onClick={exportarPDF}
            aria-label="Exportar PDF"
          >
            {comIcones ? <FileDown className="h-4 w-4" /> : "PDF"}
          </Button>
          <Button
            variant="outline"
            size={comIcones ? "icon" : "default"}
            onClick={() => void compartilhar()}
            aria-label="Compartilhar"
          >
            {comIcones ? <Share2 className="h-4 w-4" /> : "Compartilhar"}
          </Button>
        </>
      )}
    </div>
  );

  return (
    <div className="space-y-3">
      {botoes}

      {mostrarTabela && (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="p-2 text-left">Insumo</th>
                <th className="p-2 text-right">Qtde</th>
                <th className="p-2 text-right">Custo R$</th>
              </tr>
            </thead>
            <tbody>
              {linhas.length === 0 && (
                <tr>
                  <td colSpan={3} className="p-3 text-muted-foreground">
                    Nenhum insumo consumido nesta data.
                  </td>
                </tr>
              )}
              {linhas.map((i, k) => (
                <tr
                  key={i.id}
                  className={`border-t border-border/60 ${k % 2 === 1 ? "bg-muted/30" : ""}`}
                >
                  <td className="p-2">{i.nome}</td>
                  <td className="p-2 text-right">
                    {qtde(i.qtd)} {i.unidade}
                  </td>
                  <td className="p-2 text-right font-medium">{dinheiro(i.custo)}</td>
                </tr>
              ))}
            </tbody>
            {linhas.length > 0 && (
              <tfoot>
                <tr className="border-t border-border bg-muted/40 font-semibold">
                  <td className="p-2" colSpan={2}>
                    Custo total
                  </td>
                  <td className="p-2 text-right">{dinheiro(total)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {imprimindo && (
        <PrintPortal>
          <h1 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>{titulo}</h1>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", borderBottom: "1px solid #000", padding: 4 }}>
                  Insumo
                </th>
                <th style={{ textAlign: "right", borderBottom: "1px solid #000", padding: 4 }}>
                  Qtde
                </th>
                <th style={{ textAlign: "right", borderBottom: "1px solid #000", padding: 4 }}>
                  Custo R$
                </th>
              </tr>
            </thead>
            <tbody>
              {linhas.map((i) => (
                <tr key={i.id}>
                  <td style={{ padding: 4, borderBottom: "1px solid #ccc" }}>{i.nome}</td>
                  <td style={{ padding: 4, borderBottom: "1px solid #ccc", textAlign: "right" }}>
                    {qtde(i.qtd)} {i.unidade}
                  </td>
                  <td style={{ padding: 4, borderBottom: "1px solid #ccc", textAlign: "right" }}>
                    {dinheiro(i.custo)}
                  </td>
                </tr>
              ))}
              <tr>
                <td style={{ padding: 4, fontWeight: 600 }}>Custo total</td>
                <td style={{ padding: 4 }} />
                <td style={{ padding: 4, textAlign: "right", fontWeight: 600 }}>
                  {dinheiro(total)}
                </td>
              </tr>
            </tbody>
          </table>
        </PrintPortal>
      )}
    </div>
  );
}
